import { upperFirst } from "lodash";
import { observer } from "mobx-react";
import React from "react";
import { TooltipComponent } from "@fidget/common/components/tooltipComponent";
import { L10n } from "@fidget/common/i18n";
import { intl } from "@fidget/common/i18n/TypedIntl";
import { getTimeString } from "@fidget/common/utils/timeFormatter";
import { IRoom } from "../../models/room/Room";
import { AgentButton } from "@fidget/common/components/inputs/buttons/agentButton/agentButton";
import { Draggable } from "../draggable";
import styles from "./scheduleButton.css";
import { IRoomMeeting } from "../../models/room/Room";
import { useRef } from "react";
import { useStore } from "../../models";

interface Props {
  icon?: string;
  room: IRoom;
  draggable?: boolean;
  showScheduledMeeting?: boolean;
}

export const ScheduleButton = observer(({ icon, room, draggable, showScheduledMeeting }: Props) => {
  const containerElement = useRef<AgentButton>();
  const { calendarStore } = useStore();

  const setContainerElement = (el?: AgentButton) => {
    if (el) {
      containerElement.current = el;
    }
  };

  const renderButton = (disabled: boolean) => {
    return (
      <AgentButton
        ref={(el) => setContainerElement(el as AgentButton)}
        id={"active_schedule_call_button"}
        icon={icon !== undefined ? icon : "icon-bot"}
        fullWidth
        disabled={disabled}
        onClick={room.startMeetingScheduling}
      >
        {room.meetingSchedulingInProgress ? (
          <L10n id={"conversation.meetingScheduler.schedulingInProgress"} />
        ) : (
          <L10n id={"calendar.meeting.scheduleCall"} />
        )}
      </AgentButton>
    );
  };

  const renderActiveButton = () => {
    return (
      <Draggable
        onDragStart={(start, offset) => {
          if (containerElement.current) {
            calendarStore.calendarEntryDragStarted(
              start,
              offset,
              containerElement.current.offsetWidth(),
              room.id
            );
          }
        }}
        onDragEnd={() => calendarStore.calendarEntryDragEnded()}
      >
        {renderButton(false)}
      </Draggable>
    );
  };

  const { hasContactData, firstName } = room.owner;
  const active =
    hasContactData && !room.meetingSchedulingInProgress && !calendarStore.schedulingDisabled;
  const tooltipText = hasContactData
    ? intl.formatMessage("calendar.meeting.tooltipdragdrop")
    : intl.formatMessage("calendar.meeting.tooltipEmailPhone", { name: firstName });
  const scheduled = room.meetings
    .filter((m: IRoomMeeting) => m.start > Date.now())
    .sort((a: IRoomMeeting, b: IRoomMeeting) => a.start - b.start);

  return (
    <div className={styles.scheduleContainer}>
      {showScheduledMeeting &&
        scheduled.length > 0 &&
        scheduled.map((meeting: IRoomMeeting) => (
          <div className={styles["scheduled-meeting"]} key={meeting.id}>
            <div className={styles["meeting-day"]}>
              {upperFirst(intl.formatRelativeDays(meeting.start))}
            </div>
            <div>
              {getTimeString(meeting.start)}-{getTimeString(meeting.start + meeting.duration)}
            </div>
            <div className={styles["meeting-with"]}>
              <L10n id={"calendar.meeting.with"} />
              <b>{meeting.agent.displayedName}</b>
            </div>
            <div className={styles["calendar-icon"]}>
              <i className={"icon-calendar"} />
            </div>
          </div>
        ))}
      <TooltipComponent
        text={tooltipText}
        width={"180px"}
        positionPreset={"bottom"}
        tooltipShow={draggable}
      >
        {draggable && active ? renderActiveButton() : renderButton(!active)}
      </TooltipComponent>
    </div>
  );
});
